/**
 * PathParameterInputs Component
 *
 * Displays input fields for path parameters in API endpoints.
 * Handles path parameters with `in: "path"` from the OpenAPI specification.
 *
 * Requirements:
 * - 13.1: Display input fields for path parameters (`in: "path"`)
 * - 13.3: Mark required parameters
 * - 13.7: Display parameter descriptions as help text
 */

import { EndpointParameter } from '../types';

export interface PathParameterInputsProps {
  /** Path parameters from the endpoint */
  parameters: EndpointParameter[];
  /** Current parameter values */
  values: Record<string, string>;
  /** Callback when parameter values change */
  onChange: (values: Record<string, string>) => void;
}

/**
 * PathParameterInputs component renders input fields for path parameters.
 *
 * @param parameters - Array of endpoint parameters (only path params will be rendered)
 * @param values - Current values for path parameters
 * @param onChange - Callback function when values change
 */
export const PathParameterInputs = ({ parameters, values, onChange }: PathParameterInputsProps) => {
  // Filter to only path parameters (in: "path")
  const pathParams = parameters.filter((param) => param.in === 'path');

  // If no path parameters, don't render anything
  if (pathParams.length === 0) {
    return null;
  }

  /**
   * Handle input change for a specific parameter
   */
  const handleChange = (paramName: string, value: string) => {
    onChange({
      ...values,
      [paramName]: value,
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Path Parameters</h3>

      <div className="space-y-3">
        {pathParams.map((param) => (
          <div key={param.name} className="space-y-1">
            <label
              htmlFor={`path-param-${param.name}`}
              className="block text-sm font-medium text-gray-700"
            >
              {param.name}
              {param.required && (
                <span className="text-red-500 ml-1" aria-label="required">
                  *
                </span>
              )}
            </label>

            <input
              id={`path-param-${param.name}`}
              type="text"
              value={values[param.name] || ''}
              onChange={(e) => handleChange(param.name, e.target.value)}
              required={param.required}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Enter ${param.name}`}
            />

            {param.description && <p className="text-sm text-gray-500 mt-1">{param.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PathParameterInputs;
